import CloseIcon from '@mui/icons-material/Close'
import { useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { IconButton } from '../buttons/IconButton'

/**
 * @description Renders a form modal component.
 *
 * @author Maria Eduarda Franke
 * @param {boolean} props.open - Determines whether the modal is open or not.
 * @param {function} props.onClose - The function to be called when the modal is closed.
 * @param {string} props.title - The title of the modal.
 * @param {string} props.id - The ID of the modal.
 * @param {string} props.description - The description of the modal.
 * @param {ReactNode} props.children - The content of the modal.
 * @returns {ReactNode} The rendered form modal component.
 */
const FormModal = ({ open, onClose, title, id, description, children, ...props }) => {

    const theme = useTheme()

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth='sm'
            {...props}
        >
            <DialogTitle>
                <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center'>
                    <Box display='flex' flexDirection='row' alignItems='baseline' gap={1}>
                        <Typography variant='h6'>{title}</Typography>
                        {id && (
                            <Typography variant='body1' color={theme.palette.text.secondary}>#{id}</Typography>
                        )}
                    </Box>
                    <IconButton color={theme.palette.text.secondary} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <DialogContentText>
                    {description}
                </DialogContentText>
                <Box mt={1}>
                    {children}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

FormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    id: PropTypes.number,
    description: PropTypes.string,
    children: PropTypes.node
}

export default FormModal
